import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { OnboardingLayout } from '../OnboardingLayout';
import { Card } from '../../../components/ui/Card';
import { Label } from '../../../components/ui/Label';
import { Input } from '../../../components/ui/Input';
import { FieldError } from '../../../components/ui/FieldError';
import { Alert } from '../../../components/ui/Alert';
import { LoadingState } from '../../../components/ui/Spinner';
import { ApiProblemError, postQuote, type Quote } from '../../../lib/api-client';
import { QuoteResult } from '../../quotes/components/QuoteResult';
import { useApiKeys } from '../ApiKeysContext';
import { useOnboardingState } from '../useOnboardingState';

// Illustrative example addresses (Av. Paulista/SP -> Centro/RJ) — chosen
// only so the demo quote works with zero typing, per the acceptance
// criterion ("formulário pré-preenchido"); both fields stay fully editable.
const DEFAULT_FROM_POSTAL_CODE = '01310930';
const DEFAULT_TO_POSTAL_CODE = '20040020';
const DEFAULT_PACKAGE = { weight_g: 1000, height_cm: 20, width_cm: 20, length_cm: 20 };

/**
 * Tela 4 — Cotação demo (/onboarding/demo). Which API key authenticates
 * `POST /v1/quotes` follows the rule decided for this onboarding
 * (ApiKeysContext.tsx docstring): `trv_live_` when Tela 3 connected at
 * least one provider (real quote), `trv_test_` otherwise (sandbox — zero
 * real provider call, CLAUDE.md domain rule #4).
 */
export function DemoStep() {
  const fromId = useId();
  const toId = useId();
  const weightId = useId();

  const { keys } = useApiKeys();
  const { state, update } = useOnboardingState();
  const navigate = useNavigate();

  const isSandbox = state.connectedProviders.length === 0;
  const activeKey = isSandbox ? keys.test : keys.live;

  const [fromPostalCode, setFromPostalCode] = useState(state.fromPostalCode ?? DEFAULT_FROM_POSTAL_CODE);
  const [toPostalCode, setToPostalCode] = useState(DEFAULT_TO_POSTAL_CODE);
  const [weightG, setWeightG] = useState(String(DEFAULT_PACKAGE.weight_g));

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const digitsFrom = fromPostalCode.replace(/\D/g, '');
    const digitsTo = toPostalCode.replace(/\D/g, '');
    const weight = Number(weightG);

    const nextErrors: Record<string, string | undefined> = {};
    if (digitsFrom.length !== 8) nextErrors.from_postal_code = 'CEP de origem deve ter 8 dígitos.';
    if (digitsTo.length !== 8) nextErrors.to_postal_code = 'CEP de destino deve ter 8 dígitos.';
    if (!Number.isFinite(weight) || weight <= 0) nextErrors.weight_g = 'Peso inválido.';
    if (Object.values(nextErrors).some(Boolean)) {
      setFieldErrors(nextErrors);
      return;
    }

    if (!activeKey) {
      setFormError('As chaves de API do onboarding ainda não estão prontas. Volte à etapa anterior e tente novamente.');
      return;
    }

    setLoading(true);
    setQuote(null);
    try {
      const result = await postQuote(activeKey.key, {
        from_postal_code: digitsFrom,
        to_postal_code: digitsTo,
        packages: [{ ...DEFAULT_PACKAGE, weight_g: weight }],
      });
      setQuote(result);
    } catch (err) {
      if (err instanceof ApiProblemError) {
        const problemFieldErrors: Record<string, string | undefined> = {};
        for (const fieldErr of err.problem.errors ?? []) {
          problemFieldErrors[fieldErr.field] = fieldErr.message;
        }
        if (Object.keys(problemFieldErrors).length > 0) {
          setFieldErrors(problemFieldErrors);
        } else {
          setFormError(err.problem.detail ?? err.problem.title);
        }
      } else {
        setFormError('Não foi possível cotar agora. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleContinue() {
    update({ step: 'api-key', fromPostalCode: fromPostalCode.replace(/\D/g, '') });
    navigate('/onboarding/api-key');
  }

  return (
    <OnboardingLayout
      step="demo"
      title="Faça uma cotação de teste"
      description="Compare preço e prazo entre transportadoras antes de sair usando o Trivio de verdade."
    >
      <Alert variant={isSandbox ? 'warning' : 'info'} title={isSandbox ? 'Cotação simulada (sandbox)' : 'Cotação real'}>
        {isSandbox
          ? 'Você ainda não conectou nenhum provedor — esta cotação usa dados simulados, sem chamada real a nenhuma transportadora.'
          : 'Você conectou pelo menos um provedor — esta cotação chama a API real dos provedores conectados.'}
      </Alert>

      <Card className="p-6">
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-3" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor={fromId}>CEP de origem</Label>
            <Input
              id={fromId}
              inputMode="numeric"
              value={fromPostalCode}
              invalid={Boolean(fieldErrors.from_postal_code)}
              aria-describedby={fieldErrors.from_postal_code ? `${fromId}-error` : undefined}
              onChange={(event) => setFromPostalCode(event.target.value)}
            />
            <FieldError id={`${fromId}-error`} message={fieldErrors.from_postal_code} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={toId}>CEP de destino</Label>
            <Input
              id={toId}
              inputMode="numeric"
              value={toPostalCode}
              invalid={Boolean(fieldErrors.to_postal_code)}
              aria-describedby={fieldErrors.to_postal_code ? `${toId}-error` : undefined}
              onChange={(event) => setToPostalCode(event.target.value)}
            />
            <FieldError id={`${toId}-error`} message={fieldErrors.to_postal_code} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={weightId}>Peso (g)</Label>
            <Input
              id={weightId}
              inputMode="numeric"
              value={weightG}
              invalid={Boolean(fieldErrors.weight_g)}
              aria-describedby={fieldErrors.weight_g ? `${weightId}-error` : undefined}
              onChange={(event) => setWeightG(event.target.value)}
            />
            <FieldError id={`${weightId}-error`} message={fieldErrors.weight_g} />
          </div>

          <p className="col-span-full text-sm text-muted-foreground">
            Pacote de exemplo: {DEFAULT_PACKAGE.height_cm}×{DEFAULT_PACKAGE.width_cm}×{DEFAULT_PACKAGE.length_cm} cm.
          </p>

          <Button type="submit" variant="default" disabled={loading} className="col-span-full self-start sm:w-auto">
            {loading ? 'Cotando…' : 'Cotar'}
          </Button>
        </form>
      </Card>

      {formError ? (
        <Alert variant="error" title="Não foi possível cotar">
          {formError}
        </Alert>
      ) : null}

      {loading ? <LoadingState label="Buscando ofertas…" /> : null}

      {quote ? <QuoteResult quote={quote} /> : null}

      <div className="flex justify-end border-t border-border pt-6">
        <Button type="button" variant="default" disabled={!quote} onClick={handleContinue}>
          Continuar
        </Button>
      </div>
    </OnboardingLayout>
  );
}

